import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, UserX, User as UserIcon, Copy, Check, ShieldAlert, Shield, ShieldPlus, ShieldMinus, Coins, Plus, Minus, KeyRound, ClipboardList, RefreshCcw, UserCheck, UserMinus, Clock, FileDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const AUDIT_COLOR_MAP = {
  sky: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  purple: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  slate: "border-slate-600 bg-slate-800/60 text-slate-300",
};
function logColorClass(color) {
  return AUDIT_COLOR_MAP[color] || AUDIT_COLOR_MAP.slate;
}

export default function AdminMagnati() {
  const [loading, setLoading] = useState(true);  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [openTeam, setOpenTeam] = useState(null); // team object being edited
  const [form, setForm] = useState({ email: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null); // { email, password }
  const [copied, setCopied] = useState(false);
  const [admins, setAdmins] = useState(null); // { owner, co_admins, current_user_is_owner }
  const [resetResult, setResetResult] = useState(null); // { email, name, temp_password, team }
  const [resetCopied, setResetCopied] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [lg, t] = await Promise.all([
        api.get("/magnati/lega"),
        api.get("/magnati/lega/teams"),
      ]);
      setLeague(lg.data);
      setTeams(t.data.teams || []);
      // Load admins list
      try {
        const a = await api.get(`/leagues/${lg.data.league_id}/admins`);
        setAdmins(a.data);
      } catch (_) { /* ignore if not member */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function grantAdmin(team) {
    const mgr = team.current_manager;
    if (!mgr) return toast.error("Il team non ha un manager assegnato");
    if (!window.confirm(`Vuoi promuovere ${mgr.name} a co-admin? Potrà gestire assegnazioni manager come te.`)) return;
    try {
      const r = await api.post(`/leagues/${league.league_id}/admins/grant`, { user_id: mgr.user_id });
      if (r.data.already_admin) toast.info(`${mgr.name} era già co-admin`);
      else toast.success(`${mgr.name} promosso a co-admin`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function revokeAdmin(userId, userName) {
    if (!window.confirm(`Rimuovere i privilegi di co-admin a ${userName}? Rimarrà comunque manager del suo team.`)) return;
    try {
      await api.post(`/leagues/${league.league_id}/admins/revoke`, { user_id: userId });
      toast.success(`Privilegi rimossi a ${userName}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function adjustCredits(team, delta) {
    const reason = window.prompt(
      `Aggiustamento crediti per "${team.name}"\n\nBudget attuale: ${team.budget?.toFixed(0) || 0}\nModifica: ${delta > 0 ? "+" : ""}${delta}\nNuovo budget: ${((team.budget || 0) + delta).toFixed(0)}\n\nInserisci il motivo (obbligatorio):`,
      ""
    );
    if (!reason || !reason.trim()) return;
    try {
      const r = await api.post(`/leagues/${league.league_id}/teams/${team.team_id}/adjust-credits`, {
        delta, reason: reason.trim()
      });
      toast.success(`${team.name}: ${r.data.old_budget} → ${r.data.new_budget} (${delta > 0 ? "+" : ""}${delta})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function customAdjust(team) {
    const raw = window.prompt(`Modifica crediti "${team.name}"\nInserisci delta (es. +50 o -20):`, "");
    if (!raw) return;
    const delta = Number(raw);
    if (isNaN(delta) || delta === 0) return toast.error("Delta non valido");
    return adjustCredits(team, delta);
  }

  function openAssign(team) {
    setOpenTeam(team);
    setForm({
      email: `${(team.expected_manager_name || "").toLowerCase().replace(/[^a-z]+/g, ".").replace(/^\.|\.$/g, "")}@magnati.it`,
      name: team.expected_manager_name || "",
    });
    setLastCreated(null);
  }

  async function submit() {
    if (!openTeam) return;
    if (!form.email.trim()) return toast.error("Email obbligatoria");
    setSubmitting(true);
    try {
      const r = await api.post("/magnati/lega/assign", {
        team_id: openTeam.team_id,
        user_email: form.email.trim(),
        user_name: form.name.trim() || null,
        create_if_missing: true,
      });
      toast.success(`Assegnato: ${r.data.assigned_user.name} → ${openTeam.name}`);
      if (r.data.created_new_user && r.data.initial_password) {
        setLastCreated({ email: r.data.assigned_user.email, password: r.data.initial_password, team: openTeam.name });
      } else {
        setOpenTeam(null);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  }

  async function unassign(team) {
    if (!window.confirm(`Rimuovere il manager da "${team.name}"? L'account utente NON verrà eliminato.`)) return;
    try {
      await api.post("/magnati/lega/unassign", { team_id: team.team_id });
      toast.success(`Manager rimosso da ${team.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function resetPassword(team) {
    const mgr = team.current_manager;
    if (!mgr) return toast.error("Il team non ha un manager assegnato");
    if (!window.confirm(
      `Reset password per ${mgr.name} (${mgr.email || "email nascosta"})?\n\n` +
      `Verrà generata una password temporanea sicura che ti sarà mostrata UNA SOLA VOLTA.\n` +
      `Dovrai comunicarla manualmente al manager (WhatsApp, telefono, ecc.).\n` +
      `La password attuale del manager verrà invalidata immediatamente.`
    )) return;
    try {
      const r = await api.post("/magnati/lega/reset-password", { user_id: mgr.user_id });
      setResetResult({
        email: r.data.email, name: r.data.name,
        temp_password: r.data.temp_password, team: team.name,
      });
      setResetCopied(false);
      toast.success(`Password resettata per ${r.data.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function copyResetCred() {
    if (!resetResult) return;
    navigator.clipboard.writeText(`Email: ${resetResult.email}\nPassword temporanea: ${resetResult.temp_password}`);
    setResetCopied(true);
    setTimeout(() => setResetCopied(false), 2000);
  }

  async function loadLogs() {
    setLogsLoading(true);
    try {
      const r = await api.get(`/magnati/lega/admin-log?limit=100`);
      setLogs(r.data.logs || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogsLoading(false); }
  }
  useEffect(() => { if (logsOpen && logs.length === 0) loadLogs(); }, [logsOpen]);

  async function loadOnboarding() {
    setOnboardingLoading(true);
    try {
      const r = await api.get(`/magnati/lega/onboarding-status`);
      setOnboarding(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOnboardingLoading(false); }
  }
  useEffect(() => { if (onboardingOpen && !onboarding) loadOnboarding(); }, [onboardingOpen]);

  async function downloadCredentialsPdf() {
    try {
      const r = await api.get(`/magnati/lega/export-credentials-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Credenziali_Manager_Magnati_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF scaricato");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function formatLogTime(iso) {
    if (!iso) return "?";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch (_) { return iso; }
  }

  function relativeTime(iso) {
    if (!iso) return "mai";
    try {
      const d = new Date(iso);
      const s = (Date.now() - d.getTime()) / 1000;
      if (s < 60) return "adesso";
      if (s < 3600) return `${Math.floor(s / 60)}m fa`;
      if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
      if (s < 7 * 86400) return `${Math.floor(s / 86400)}g fa`;
      if (s < 30 * 86400) return `${Math.floor(s / (7 * 86400))}sett fa`;
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
    } catch (_) { return "?"; }
  }

  function copyCred() {
    if (!lastCreated) return;
    navigator.clipboard.writeText(`Email: ${lastCreated.email}\nPassword: ${lastCreated.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const assignedCount = teams.filter(t => t.assigned).length;

  if (loading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 font-mono text-sm uppercase tracking-widest">Caricamento…</div>;
  }
  if (league && !league.is_admin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <ShieldAlert className="w-16 h-16 text-rose-500 mx-auto" />
          <h2 className="text-2xl font-bold text-slate-200">Accesso Negato</h2>
          <p className="text-slate-500">Solo l&apos;admin della lega può assegnare manager.</p>
          <Link to="/dashboard" className="text-amber-400 underline">← Torna alla Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <header className="border-b border-amber-500/20 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" data-testid="admin-back-btn" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-500/70">Admin · {league?.name || "Lega"}</div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
                Assegna Manager
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
              {assignedCount}/{teams.length} assegnati
            </Badge>
            <Link to={`/lega/${league?.league_id}`} className="text-xs font-mono text-amber-400 hover:text-amber-300 underline">
              Vai alla lega →
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Admins panel */}
        {admins && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-rose-400" /> Amministratori della Lega
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                {admins.owner && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 text-sm"
                    data-testid="admin-owner-badge">
                    <Shield className="w-3.5 h-3.5" />
                    <div>
                      <div className="font-bold">{admins.owner.name}</div>
                      <div className="text-[10px] text-rose-300/70 font-mono">Owner · {admins.owner.email}</div>
                    </div>
                  </div>
                )}
                {(admins.co_admins || []).map(ca => (
                  <div key={ca.user_id}
                    data-testid={`co-admin-${ca.user_id}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm">
                    <Shield className="w-3.5 h-3.5" />
                    <div>
                      <div className="font-bold">{ca.name}</div>
                      <div className="text-[10px] text-amber-300/70 font-mono">Co-admin · {ca.email}</div>
                    </div>
                    {admins.current_user_is_owner && (
                      <button
                        onClick={() => revokeAdmin(ca.user_id, ca.name)}
                        data-testid={`revoke-admin-${ca.user_id}`}
                        title="Rimuovi co-admin"
                        className="ml-1 p-1 rounded hover:bg-rose-500/20 text-rose-300">
                        <ShieldMinus className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {(admins.co_admins || []).length === 0 && (
                  <div className="text-xs text-slate-500 italic">Nessun co-admin. {admins.current_user_is_owner && "Clicca sull'icona scudo su un team con manager per promuoverlo."}</div>
                )}
              </div>
              {!admins.current_user_is_owner && (
                <div className="text-[10px] text-slate-500 italic">Sei co-admin. Solo il proprietario può gestire gli altri co-admin.</div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="text-sm text-slate-400">
              <span className="text-slate-500 font-mono uppercase tracking-widest text-xs mr-2">Info</span>
              Assegnando un manager a un team, il sistema crea automaticamente l&apos;utente se l&apos;email non esiste ancora e mostra la password iniziale (default: nome123).
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Status */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="onboarding-card">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setOnboardingOpen(o => !o)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-emerald-400" /> Onboarding Manager
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest ml-2">Chi ha effettivamente fatto login</span>
              </span>
              <span className="flex items-center gap-2">
                {onboarding && (
                  <span className="text-[10px] font-mono flex items-center gap-1.5">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" data-testid="onboarding-active-count">{onboarding.summary.active} attivi</span>
                    <span className="px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300" data-testid="onboarding-inactive-count">{onboarding.summary.never_logged_in} mai loggati</span>
                    {onboarding.summary.unassigned > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">{onboarding.summary.unassigned} non assegnati</span>
                    )}
                  </span>
                )}
                {onboardingOpen && (
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadCredentialsPdf(); }}
                    data-testid="download-credentials-pdf-btn"
                    title="Scarica PDF con tutte le credenziali"
                    className="p-1 px-1.5 rounded flex items-center gap-1 hover:bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-wider">
                    <FileDown className="w-3 h-3"/> PDF
                  </button>
                )}
                {onboardingOpen && (
                  <button
                    onClick={(e) => { e.stopPropagation(); loadOnboarding(); }}
                    data-testid="refresh-onboarding-btn"
                    disabled={onboardingLoading}
                    title="Ricarica"
                    className="p-1 rounded hover:bg-slate-800 text-slate-400">
                    <RefreshCcw className={`w-3.5 h-3.5 ${onboardingLoading ? "animate-spin" : ""}`} />
                  </button>
                )}
                <span className="text-xs font-mono text-emerald-400" data-testid="onboarding-toggle">
                  {onboardingOpen ? "▲ Nascondi" : "▼ Mostra"}
                </span>
              </span>
            </CardTitle>
          </CardHeader>
          {onboardingOpen && (
            <CardContent className="pt-2 space-y-2">
              {onboardingLoading && !onboarding && (
                <div className="text-center text-xs text-slate-500 py-4">Caricamento…</div>
              )}
              {onboarding && (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
                        <th className="text-left px-2 py-1.5">Team</th>
                        <th className="text-left px-2 py-1.5">Manager</th>
                        <th className="text-left px-2 py-1.5">Stato</th>
                        <th className="text-right px-2 py-1.5">Login</th>
                        <th className="text-right px-2 py-1.5">Ultimo accesso</th>
                        <th className="text-left px-2 py-1.5">Password</th>
                      </tr>
                    </thead>
                    <tbody data-testid="onboarding-table-body">
                      {onboarding.teams.map((t, idx) => {
                        const st = t.status;
                        const stBadge = st === "active"
                          ? { label: "Attivo", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", emoji: "✅" }
                          : st === "unassigned"
                            ? { label: "Non assegnato", cls: "bg-slate-800 text-slate-400 border-slate-700", emoji: "—" }
                            : { label: "Mai loggato", cls: "bg-rose-500/10 text-rose-300 border-rose-500/40", emoji: "⏳" };
                        return (
                          <tr key={t.team_id} data-testid={`onboarding-row-${idx}`}
                            className="border-b border-slate-800/60 hover:bg-slate-800/30">
                            <td className="px-2 py-2 font-bold text-slate-200">{t.team_name}</td>
                            <td className="px-2 py-2">
                              {t.manager ? (
                                <div>
                                  <div className="text-slate-100">{t.manager.name}</div>
                                  <div className="text-[10px] text-slate-500 font-mono">{t.manager.email}</div>
                                </div>
                              ) : <span className="text-slate-500 italic">—</span>}
                            </td>
                            <td className="px-2 py-2">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border ${stBadge.cls}`}>
                                {stBadge.emoji} {stBadge.label}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-slate-300">{t.login_count || 0}</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-400" title={t.last_login_at || "mai"}>
                              <Clock className="w-3 h-3 inline-block mr-1 text-slate-600" />{relativeTime(t.last_login_at)}
                            </td>
                            <td className="px-2 py-2">
                              {!t.manager ? <span className="text-slate-600">—</span> : (
                                t.password_changed
                                  ? <span className="text-[10px] font-mono text-emerald-400">✓ personale</span>
                                  : <span className="text-[10px] font-mono text-amber-400" title="Ancora la password default '<nome>123'">⚠ default</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-[10px] text-slate-500 italic text-center pt-2">
                Il conteggio login parte da questo iter. Manager loggati prima appaiono senza timestamp finché non rientrano.
              </div>
            </CardContent>
          )}
        </Card>

        {/* Audit Log */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="audit-log-card">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setLogsOpen(o => !o)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-sky-400" /> Log Amministratori
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest ml-2">Audit trail azioni sensibili</span>
              </span>
              <span className="flex items-center gap-2">
                {logsOpen && (
                  <button
                    onClick={(e) => { e.stopPropagation(); loadLogs(); }}
                    data-testid="refresh-audit-log-btn"
                    disabled={logsLoading}
                    title="Ricarica"
                    className="p-1 rounded hover:bg-slate-800 text-slate-400">
                    <RefreshCcw className={`w-3.5 h-3.5 ${logsLoading ? "animate-spin" : ""}`} />
                  </button>
                )}
                <span className="text-xs font-mono text-sky-400" data-testid="audit-log-toggle">
                  {logsOpen ? "▲ Nascondi" : "▼ Mostra"}
                </span>
              </span>
            </CardTitle>
          </CardHeader>
          {logsOpen && (
            <CardContent className="space-y-2 pt-2">
              {logsLoading && logs.length === 0 && (
                <div className="text-center text-xs text-slate-500 py-4">Caricamento…</div>
              )}
              {!logsLoading && logs.length === 0 && (
                <div className="text-center text-xs text-slate-500 py-4 italic">Nessuna azione registrata ancora.</div>
              )}
              {logs.length > 0 && (
                <div className="max-h-[520px] overflow-y-auto pr-1 space-y-1.5" data-testid="audit-log-list">
                  {logs.map((l, idx) => (
                    <div key={l.log_id || `${l.action}-${l.at}-${idx}`}
                      data-testid={`audit-log-entry-${idx}`}
                      className={`flex items-start gap-3 px-3 py-2 rounded-lg bg-slate-950/40 border border-slate-800 hover:border-slate-700 transition-colors`}>
                      <div className="text-lg leading-none pt-0.5" aria-hidden>{l.emoji}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${logColorClass(l.color)}`}>
                            {l.label}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono ml-auto">{formatLogTime(l.at)}</span>
                        </div>
                        <div className="text-sm text-slate-200 mt-0.5">{l.description}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          da <span className="text-slate-400">{l.actor?.name || "?"}</span>
                          {l.actor?.email && <span className="ml-1">({l.actor.email})</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-slate-500 italic text-center pt-1">
                Mostro le ultime {logs.length} azioni sensibili. Aggiornato in tempo reale — clicca ↻ per ricaricare.
              </div>
            </CardContent>
          )}
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          {teams.map(team => (
            <Card key={team.team_id}
              data-testid={`admin-team-${team.magnati_club_slug}`}
              className={`bg-slate-900/60 ${team.assigned ? "border-emerald-500/30" : "border-amber-500/30"}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{team.name}</CardTitle>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                      <span>Rosa: {team.roster_count} giocatori</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1 text-amber-400 font-mono font-bold">
                        <Coins className="w-3 h-3"/>{team.budget?.toFixed(0) || 0}
                      </span>
                    </div>
                  </div>
                  {team.assigned ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Assegnato</Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">Libero</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Atteso</div>
                    <div className="text-slate-300 flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5 text-amber-400"/>
                      {team.expected_manager_name || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Corrente</div>
                    {team.current_manager ? (
                      <div className="text-emerald-300 flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5"/>
                        <div>
                          <div>{team.current_manager.name}</div>
                          {team.current_manager.email && <div className="text-[10px] text-slate-500">{team.current_manager.email}</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-600 italic">Non assegnato</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => openAssign(team)}
                    data-testid={`assign-btn-${team.magnati_club_slug}`}
                    className={`flex-1 ${team.assigned ? "bg-slate-800 hover:bg-slate-700 text-slate-200" : "bg-amber-500 hover:bg-amber-400 text-slate-950"} text-xs font-bold`}>
                    <UserPlus className="w-3.5 h-3.5 mr-1.5"/>
                    {team.assigned ? "Riassegna" : "Assegna"}
                  </Button>
                  {team.assigned && admins?.current_user_is_owner && team.current_manager
                    && team.current_manager.user_id !== admins.owner?.user_id
                    && !(admins.co_admins || []).some(ca => ca.user_id === team.current_manager.user_id) && (
                    <Button
                      onClick={() => grantAdmin(team)}
                      data-testid={`grant-admin-btn-${team.magnati_club_slug}`}
                      variant="outline"
                      title="Rendi co-admin"
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-xs">
                      <ShieldPlus className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                  {team.assigned && (
                    <Button
                      onClick={() => resetPassword(team)}
                      data-testid={`reset-password-btn-${team.magnati_club_slug}`}
                      variant="outline"
                      title="Reset password manager"
                      className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10 text-xs">
                      <KeyRound className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                  {team.assigned && (
                    <Button
                      onClick={() => unassign(team)}
                      data-testid={`unassign-btn-${team.magnati_club_slug}`}
                      variant="outline"
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-xs">
                      <UserX className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                </div>
                {/* Credit adjustment (admin/co-admin only) */}
                <div className="flex items-center gap-1 pt-2 border-t border-slate-800">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mr-1 flex items-center gap-1">
                    <Coins className="w-3 h-3 text-amber-400"/> Crediti
                  </span>
                  <button onClick={() => adjustCredits(team, -50)} data-testid={`credit-minus50-${team.magnati_club_slug}`}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30">-50</button>
                  <button onClick={() => adjustCredits(team, -10)} data-testid={`credit-minus10-${team.magnati_club_slug}`}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30">-10</button>
                  <button onClick={() => adjustCredits(team, +10)} data-testid={`credit-plus10-${team.magnati_club_slug}`}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">+10</button>
                  <button onClick={() => adjustCredits(team, +50)} data-testid={`credit-plus50-${team.magnati_club_slug}`}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">+50</button>
                  <button onClick={() => customAdjust(team)} data-testid={`credit-custom-${team.magnati_club_slug}`}
                    className="ml-auto px-2 py-0.5 rounded text-[10px] font-mono bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                    Custom…
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Assignment Modal */}
      {openTeam && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { if (!lastCreated) setOpenTeam(null); }}>
          <div className="w-full max-w-md rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/30 shadow-2xl"
            onClick={e => e.stopPropagation()} data-testid="assign-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400/80">Assegna Manager</div>
              <div className="text-xl font-bold text-slate-100 mt-1">{openTeam.name}</div>
              <div className="text-sm text-slate-500 mt-0.5">Atteso: {openTeam.expected_manager_name}</div>
            </div>

            {lastCreated ? (
              <div className="p-5 space-y-3">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="text-emerald-300 font-bold mb-2">✓ Utente creato con successo!</div>
                  <div className="text-sm text-slate-300 space-y-1">
                    <div><span className="text-slate-500">Team:</span> {lastCreated.team}</div>
                    <div><span className="text-slate-500">Email:</span> <code className="text-emerald-300">{lastCreated.email}</code></div>
                    <div><span className="text-slate-500">Password iniziale:</span> <code className="text-emerald-300">{lastCreated.password}</code></div>
                  </div>
                  <Button onClick={copyCred} data-testid="copy-credentials-btn"
                    className="mt-3 w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40">
                    {copied ? <><Check className="w-4 h-4 mr-2"/> Copiato!</> : <><Copy className="w-4 h-4 mr-2"/> Copia credenziali</>}
                  </Button>
                  <div className="text-[10px] text-slate-500 mt-2 italic">
                    Invia queste credenziali al manager. Potrà cambiare la password dopo il primo login.
                  </div>
                </div>
                <Button onClick={() => { setOpenTeam(null); setLastCreated(null); }} className="w-full">Chiudi</Button>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">Email del manager</label>
                    <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      data-testid="assign-email-input"
                      placeholder="manager@example.it"
                      className="bg-slate-950 border-slate-700 text-slate-100"/>
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">Nome (per nuovo utente)</label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      data-testid="assign-name-input"
                      placeholder={openTeam.expected_manager_name || "Nome Cognome"}
                      className="bg-slate-950 border-slate-700 text-slate-100"/>
                    <div className="text-[10px] text-slate-500 mt-1">Se l&apos;email non esiste, verrà creato un nuovo account con password iniziale.</div>
                  </div>
                </div>
                <div className="p-5 border-t border-slate-800 flex gap-2">
                  <Button variant="outline" onClick={() => setOpenTeam(null)} className="flex-1">Annulla</Button>
                  <Button onClick={submit} disabled={submitting} data-testid="assign-submit-btn"
                    className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
                    {submitting ? "…" : "Conferma"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Reset Password Result Modal */}
      {resetResult && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {}}>
          <div className="w-full max-w-md rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-sky-500/30 shadow-2xl"
            onClick={e => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs font-mono uppercase tracking-widest text-sky-400/80 flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5"/> Password Resettata
              </div>
              <div className="text-xl font-bold text-slate-100 mt-1">{resetResult.name}</div>
              <div className="text-sm text-slate-500 mt-0.5">Team: {resetResult.team}</div>
            </div>
            <div className="p-5 space-y-3">
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="text-amber-300 font-bold mb-2 text-sm">⚠️ Salva subito queste credenziali</div>
                <div className="text-[11px] text-amber-200/80 leading-relaxed">
                  Questa password temporanea è mostrata UNA SOLA VOLTA e non è più recuperabile dopo aver chiuso questa finestra. La password precedente del manager è già stata invalidata.
                </div>
              </div>
              <div className="p-4 rounded-lg bg-sky-500/10 border border-sky-500/30 space-y-1.5 text-sm">
                <div><span className="text-slate-500">Email:</span> <code className="text-sky-300" data-testid="reset-password-email">{resetResult.email}</code></div>
                <div><span className="text-slate-500">Password temp.:</span> <code className="text-sky-300 font-bold" data-testid="reset-password-value">{resetResult.temp_password}</code></div>
              </div>
              <Button onClick={copyResetCred} data-testid="copy-reset-credentials-btn"
                className="w-full bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/40">
                {resetCopied ? <><Check className="w-4 h-4 mr-2"/> Copiato!</> : <><Copy className="w-4 h-4 mr-2"/> Copia credenziali</>}
              </Button>
              <div className="text-[10px] text-slate-500 italic text-center">
                Comunica queste credenziali al manager (WhatsApp/telefono). Al login potrà cambiare la password dal proprio profilo.
              </div>
              <Button onClick={() => { setResetResult(null); setResetCopied(false); }}
                data-testid="close-reset-modal-btn"
                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200">
                Ho salvato, chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
